import axios from "axios";
import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import styled from "styled-components";
import Button from "../../common/Button";
import appConfig from "../../config/appConfig";

const Form = styled.form`
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
`;

const Row = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;
  font-weight: 500;

  input {
    font: inherit;
    padding: 0.6rem 0.75rem;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    background: #fff;
  }
`;

const SigWrap = styled.div`
  display: grid;
  gap: 0.35rem;
  font-weight: 500;
`;

const SigBox = styled.div`
  border: 1px solid var(--color-border);
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  canvas {
    display: block;
    height: 160px !important;
  }
`;

const SigActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.25rem;
`;

const GhostButton = styled.button`
  font: inherit;
  font-size: 0.8rem;
  background: none;
  border: 1px solid var(--color-border);
  border-radius: 4px;
  padding: 0.2rem 0.6rem;
  cursor: pointer;
  color: var(--color-muted);
  &:hover {
    background: #f0f0f0;
  }
`;

const Check = styled.label`
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
  font-weight: 400;
  line-height: 1.45;
  input {
    margin-top: 0.25rem;
  }
`;

const Honeypot = styled.div`
  position: absolute;
  left: -10000px;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const Status = styled.p<{ error?: boolean }>`
  color: ${(p) => (p.error ? "var(--color-error)" : "var(--color-success)")};
  font-weight: 500;
`;

const Success = styled.div`
  border: 1px solid var(--color-success);
  background: #ecfdf5;
  padding: 1.25rem;
  border-radius: 6px;
`;

type FormState = {
  fullName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  pilotInitials: string;
  emergencyContact: string;
  emergencyContactPhone: string;
  acceptedRules: boolean;
  acceptedSiteBriefing: boolean;
  acceptedWaiver: boolean;
  website: string; // honeypot
};

const empty: FormState = {
  fullName: "",
  dateOfBirth: "",
  email: "",
  phone: "",
  pilotInitials: "",
  emergencyContact: "",
  emergencyContactPhone: "",
  acceptedRules: false,
  acceptedSiteBriefing: false,
  acceptedWaiver: false,
  website: "",
};

/**
 * Builds a Venmo deeplink with web fallback. The `note` carries `waiverId` so
 * we can reconcile a Venmo payment back to a stored signature by hand.
 */
function venmoLink(waiverId: string) {
  const handle = appConfig.flyIn.venmoHandle;
  // venmo.com handles both mobile deeplink redirects and web fallback.
  return `https://venmo.com/u/${encodeURIComponent(handle)}`;
}

function WaiverForm() {
  const [form, setForm] = useState<FormState>(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waiverId, setWaiverId] = useState<string | null>(null);
  const sigRef = useRef<SignatureCanvas>(null);
  const sigBoxRef = useRef<HTMLDivElement>(null);
  const [sigWidth, setSigWidth] = useState(600);

  useEffect(() => {
    if (!sigBoxRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setSigWidth(Math.floor(w));
    });
    observer.observe(sigBoxRef.current);
    return () => observer.disconnect();
  }, []);

  const update = (key: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.fullName.trim() || !form.dateOfBirth || !form.email.trim() || !form.phone.trim() ||
        !form.pilotInitials.trim() || !form.emergencyContact.trim() || !form.emergencyContactPhone.trim()) {
      setError("Please fill in all required fields.");
      return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setError("Please draw your signature before continuing.");
      return;
    }
    if (!form.acceptedRules || !form.acceptedSiteBriefing || !form.acceptedWaiver) {
      setError("Please accept all checkboxes before signing.");
      return;
    }

    setSubmitting(true);
    try {
      const base = appConfig.apis.waiverBaseUrl;
      if (!base) throw new Error("Waiver API URL is not configured");
      const signatureDataUrl = sigRef.current!.toDataURL("image/png");
      const res = await axios.post<{ waiverId: string }>(
        `https://${base}/${appConfig.apis.signWaiver}`,
        {
          fullName: form.fullName,
          dateOfBirth: form.dateOfBirth,
          email: form.email,
          phone: form.phone,
          pilotInitials: form.pilotInitials,
          emergencyContact: form.emergencyContact,
          emergencyContactPhone: form.emergencyContactPhone,
          signatureImage: signatureDataUrl,
          acceptedRules: form.acceptedRules,
          acceptedSiteBriefing: form.acceptedSiteBriefing,
          acceptedWaiver: form.acceptedWaiver,
          eventDate: appConfig.flyIn.eventDate,
          website: form.website,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      setWaiverId(res.data.waiverId);
    } catch (err) {
      console.error(err);
      setError(
        "Something went wrong saving your waiver. Please try again, or contact us if it keeps failing."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (waiverId) {
    const link = venmoLink(waiverId);
    return (
      <Success>
        <h3>Waiver received — last step: we're charging a small fee to cover the porta potty, refreshments, and other event costs.</h3>
        <p>
          Complete registration by sending 
          ${appConfig.flyIn.feeUsd} to <strong>@{appConfig.flyIn.venmoHandle}</strong>{" "}.
        </p>
        <p>
          <Button as="a" href={link} target="_blank" rel="noopener noreferrer">
            Open Venmo
          </Button>
        </p>
        <p style={{ fontSize: "0.9rem", color: "var(--color-muted)" }}>
          If the button doesn't work, search <strong>@{appConfig.flyIn.venmoHandle}</strong> in
          Venmo.
        </p>
      </Success>
    );
  }

  return (
    <Form onSubmit={onSubmit} noValidate>
      <Row>
        <Field>
          Full legal name
          <input
            type="text"
            required
            value={form.fullName}
            onChange={update("fullName")}
            maxLength={200}
          />
        </Field>
        <Field>
          Date of birth
          <input
            type="date"
            required
            value={form.dateOfBirth}
            onChange={update("dateOfBirth")}
          />
        </Field>
      </Row>
      <Row>
        <Field>
          Email
          <input
            type="email"
            required
            value={form.email}
            onChange={update("email")}
            maxLength={200}
          />
        </Field>
        <Field>
          Phone
          <input
            type="tel"
            required
            value={form.phone}
            onChange={update("phone")}
            maxLength={40}
          />
        </Field>
      </Row>

      <Row>
        <Field>
          Emergency contact name
          <input
            type="text"
            required
            value={form.emergencyContact}
            onChange={update("emergencyContact")}
            maxLength={200}
          />
        </Field>
        <Field>
          Emergency contact phone
          <input
            type="tel"
            required
            value={form.emergencyContactPhone}
            onChange={update("emergencyContactPhone")}
            maxLength={40}
          />
        </Field>
      </Row>

      <Check>
        <input
          type="checkbox"
          required
          checked={form.acceptedWaiver}
          onChange={update("acceptedWaiver")}
        />
        <span>
          I have read and agree to the above{" "}
          <a href="/waiver.pdf" target="_blank" rel="noopener noreferrer">
            RELEASE OF LIABILITY, ASSUMPTION OF RISK, AND INDEMNIFICATION AGREEMENT
          </a>.
        </span>
      </Check>

      <Check>
        <input
          type="checkbox"
          required
          checked={form.acceptedRules}
          onChange={update("acceptedRules")}
        />
        <span>
          I have read and agree to abide by the fly-in rules listed above.
        </span>
      </Check>
      <Check>
        <input
          type="checkbox"
          required
          checked={form.acceptedSiteBriefing}
          onChange={update("acceptedSiteBriefing")}
        />
        <span>
          I have reviewed the site briefing (airspace, hazards, launch and
          land areas) and accept full responsibility for my flying.
        </span>
      </Check>

      <Field>
        Pilot initials (Section 6 — operational responsibility)
        <input
          type="text"
          required
          value={form.pilotInitials}
          onChange={update("pilotInitials")}
          maxLength={10}
          placeholder="e.g. J.Q.P."
          style={{ maxWidth: 140 }}
        />
      </Field>

      <SigWrap>
        <span>Draw your signature below</span>
        <SigBox ref={sigBoxRef}>
          <SignatureCanvas
            ref={sigRef}
            penColor="#111"
            canvasProps={{ width: sigWidth, height: 160 }}
          />
        </SigBox>
        <SigActions>
          <GhostButton type="button" onClick={() => sigRef.current?.clear()}>
            Clear
          </GhostButton>
        </SigActions>
      </SigWrap>

      {/* Honeypot */}
      <Honeypot aria-hidden="true">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={update("website")}
          />
        </label>
      </Honeypot>

      <div>
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : "Sign waiver & continue to Venmo"}
        </Button>
      </div>
      {error && <Status error>{error}</Status>}
      <p style={{ fontSize: "0.85rem", color: "var(--color-muted)" }}>
        By signing, you acknowledge your drawn signature above is the legal
        equivalent of your handwritten signature for purposes of this waiver.
      </p>
    </Form>
  );
}

export default WaiverForm;
